import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Chip,
    TextField,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    useTheme,
    useMediaQuery,
    Snackbar,
    Alert,
} from '@mui/material';
import { Factory, X, Plus, Pencil, Trash2 } from 'lucide-react';
import { httpClient } from '../../common/httpclient';
import DefaultTable from '../../component/table/DefaultTable';
import DialogForm from '../../component/dialog/DialogForm';
import DialogConfirm from '../../component/dialog/DialogConfirm';
import { standaloneFieldSx } from '../../common/fieldStyles';
import MobileManufacturers from '../../component/vehicle/MobileManufacturers';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';

// Modal central de gestão de fabricantes (CRUD simples: listar/adicionar/
// editar/remover), DESIGN-SYSTEM.md §5.6 "Center modal", mesmo padrão de
// sub-dialog de subcategorias de `prototipo-lista-categorias.html`.
// Reaproveitado por `vehicles/List.js` (botão "Fabricantes" da toolbar) e
// embutido em `vehicles/Form.js` (link "Gerenciar fabricantes" da seção
// "Dados do veículo") — ao fechar a partir do formulário, o `<select>`
// Fabricante e o preview ao vivo são repopulados/recalculados
// (`handleCloseManufacturersModal`).
export default function ManufacturersModal({ open, onClose }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isMobileShell = useIsMobileShell();
    const [manufacturers, setManufacturers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
    const [editingManufacturer, setEditingManufacturer] = useState(null);
    const [deletingManufacturerId, setDeletingManufacturerId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type_vehicle: 'car'
    });
    const [formErrors, setFormErrors] = useState({
        name: false,
        description: false
    });
    const [saving, setSaving] = useState(false);
    const [messageSnackBar, setMessageSnackBar] = useState('');
    const [severity, setSeverity] = useState('success');
    const [openSnackBar, setOpenSnackBar] = useState(false);

    useEffect(() => {
        if (open) {
            fetchManufacturers();
        }
    }, [open]);

    const fetchManufacturers = async () => {
        try {
            setLoading(true);
            const response = await httpClient.get('/vehicles/manufacturers/enterprise');
            if (response.status === 200) {
                setManufacturers(response.data || []);
            } else {
                showSnackbar('Erro ao carregar fabricantes', 'error');
            }
        } catch (error) {
            console.error('Error fetching manufacturers:', error);
            showSnackbar('Erro ao carregar fabricantes', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showSnackbar = (message, severityType) => {
        setMessageSnackBar(message);
        setSeverity(severityType);
        setOpenSnackBar(true);
    };

    const handleOpenAddModal = () => {
        setFormData({
            name: '',
            description: '',
            type_vehicle: 'car'
        });
        setFormErrors({
            name: false,
            description: false
        });
        setOpenAddModal(true);
    };

    const handleOpenEditModal = (manufacturer) => {
        setEditingManufacturer(manufacturer);
        setFormData({
            name: manufacturer.name || '',
            description: manufacturer.description || '',
            type_vehicle: manufacturer.type_vehicle || 'car'
        });
        setFormErrors({
            name: false,
            description: false
        });
        setOpenEditModal(true);
    };

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
        setFormData({
            name: '',
            description: '',
            type_vehicle: 'car'
        });
        setFormErrors({
            name: false,
            description: false
        });
    };

    const handleCloseEditModal = () => {
        setOpenEditModal(false);
        setEditingManufacturer(null);
        setFormData({
            name: '',
            description: '',
            type_vehicle: 'car'
        });
        setFormErrors({
            name: false,
            description: false
        });
    };

    const validateForm = () => {
        const errors = {
            name: !formData.name || formData.name.trim() === '',
            description: !formData.description || formData.description.trim() === ''
        };
        setFormErrors(errors);
        return !errors.name && !errors.description;
    };

    const handleSubmitAdd = async (event) => {
        event.preventDefault();
        if (!validateForm()) {
            return;
        }

        try {
            setSaving(true);
            const response = await httpClient.post('/vehicles/manufacturers', {
                name: formData.name.trim(),
                description: formData.description.trim(),
                type_vehicle: formData.type_vehicle
            });

            if (response.status === 201 || response.status === 200) {
                showSnackbar('Fabricante criado com sucesso!', 'success');
                handleCloseAddModal();
                fetchManufacturers();
            } else {
                showSnackbar('Erro ao criar fabricante', 'error');
            }
        } catch (error) {
            console.error('Error creating manufacturer:', error);
            showSnackbar('Erro ao criar fabricante', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleSubmitEdit = async (event) => {
        event.preventDefault();
        if (!validateForm()) {
            return;
        }

        try {
            setSaving(true);
            const response = await httpClient.put(`/vehicles/manufacturers/${editingManufacturer.id}`, {
                name: formData.name.trim(),
                description: formData.description.trim(),
                type_vehicle: formData.type_vehicle
            });

            if (response.status === 200 || response.status === 204) {
                showSnackbar('Fabricante atualizado com sucesso!', 'success');
                handleCloseEditModal();
                fetchManufacturers();
            } else {
                showSnackbar('Erro ao atualizar fabricante', 'error');
            }
        } catch (error) {
            console.error('Error updating manufacturer:', error);
            showSnackbar('Erro ao atualizar fabricante', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteClick = (manufacturer) => {
        setDeletingManufacturerId(manufacturer.id);
        setOpenDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setLoading(true);
            const response = await httpClient.delete(`/vehicles/manufacturers/${deletingManufacturerId}`);

            if (response.status === 200 || response.status === 204) {
                showSnackbar('Fabricante excluído com sucesso!', 'success');
                fetchManufacturers();
            } else {
                showSnackbar('Erro ao excluir fabricante', 'error');
            }
        } catch (error) {
            console.error('Error deleting manufacturer:', error);
            showSnackbar('Erro ao excluir fabricante', 'error');
        } finally {
            setLoading(false);
            setOpenDeleteConfirm(false);
            setDeletingManufacturerId(null);
        }
    };

    const getTypeVehicleLabel = (type) => {
        const labels = {
            car: 'Carro',
            motorcycle: 'Moto',
            bicycle: 'Bicicleta'
        };
        return labels[type] || type;
    };

    const getTypeVehicleColor = (type) => {
        const colors = {
            car: 'primary',
            motorcycle: 'secondary',
            bicycle: 'success'
        };
        return colors[type] || 'default';
    };

    const columns = [
        {
            name: 'Nome',
            path: 'name'
        },
        {
            name: 'Descrição',
            path: 'description'
        },
        {
            name: 'Tipo de Veículo',
            path: 'type_vehicle',
            render: (row) => (
                <Chip
                    label={getTypeVehicleLabel(row.type_vehicle)}
                    color={getTypeVehicleColor(row.type_vehicle)}
                    size="small"
                    sx={{ fontWeight: 500 }}
                />
            )
        }
    ];

    const actions = [
        {
            icon: (row) => <Pencil size={15} strokeWidth={2} color={row.editable === false ? theme.palette.text.disabled : theme.palette.text.secondary} />,
            props: {
                onClick: (row) => {
                    if (row.editable !== false) {
                        handleOpenEditModal(row);
                    }
                },
                tooltip: 'Editar'
            }
        },
        {
            icon: (row) => <Trash2 size={15} strokeWidth={2} color={row.editable === false ? theme.palette.text.disabled : theme.palette.error.main} />,
            props: {
                onClick: (row) => {
                    if (row.editable !== false) {
                        handleDeleteClick(row);
                    }
                },
                tooltip: 'Excluir'
            }
        }
    ];

    // Shell mobile: tela do protótipo `veiculos/montadoras.html` (mesmo CRUD, em tela cheia).
    if (isMobileShell) {
        return (
            <>
                <MobileManufacturers
                    open={open}
                    onClose={onClose}
                    manufacturers={manufacturers}
                    loading={loading}
                    onNew={handleOpenAddModal}
                    onEdit={handleOpenEditModal}
                    onDelete={handleDeleteClick}
                    form={{
                        open: openAddModal || openEditModal,
                        editing: openEditModal,
                        onClose: openEditModal ? handleCloseEditModal : handleCloseAddModal,
                        onSubmit: () => (openEditModal ? handleSubmitEdit : handleSubmitAdd)({ preventDefault: () => {} }),
                        saving,
                        data: formData,
                        errors: formErrors,
                        onChange: (next, field) => {
                            setFormData(next);
                            if (field) setFormErrors({ ...formErrors, [field]: false });
                        },
                    }}
                />
                <DialogConfirm
                    open={openDeleteConfirm}
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => {
                        setOpenDeleteConfirm(false);
                        setDeletingManufacturerId(null);
                    }}
                    message="Tem certeza que deseja excluir este fabricante? Esta ação não pode ser desfeita."
                    title="Confirmar Exclusão"
                />
                <Snackbar
                    open={openSnackBar}
                    autoHideDuration={6000}
                    onClose={() => setOpenSnackBar(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                    <Alert onClose={() => setOpenSnackBar(false)} severity={severity} sx={{ width: '100%' }}>
                        {messageSnackBar}
                    </Alert>
                </Snackbar>
            </>
        );
    }

    return (
        <>
            <Dialog
                fullScreen={fullScreen}
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: `${theme.customRadius.hero}px`,
                        boxShadow: theme.customShadows.lg,
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        pb: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'secondary.bg',
                            color: 'secondary.main',
                        }}>
                            <Factory size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 17, fontWeight: 800 }}>
                                Gerenciar fabricantes
                            </Typography>
                            <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                                Liste, adicione, edite ou remova fabricantes disponíveis no campo Fabricante.
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'action.hover'
                            }
                        }}
                    >
                        <X size={18} strokeWidth={2} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            startIcon={<Plus size={16} strokeWidth={2.5} />}
                            onClick={handleOpenAddModal}
                        >
                            Adicionar Fabricante
                        </Button>
                    </Box>

                    <DefaultTable
                        colunms={columns}
                        data={manufacturers}
                        iconsActions={actions}
                        loading={loading}
                        sortableFields={['name', 'description', 'type_vehicle']}
                        defaultSort={{ field: 'name', direction: 'asc' }}
                    />
                </DialogContent>

                <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button onClick={onClose} variant="outlined">
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de Adicionar */}
            <DialogForm
                open={openAddModal}
                onClose={handleCloseAddModal}
                onSubmit={handleSubmitAdd}
                title="Novo Fabricante"
                subtitle="Preencha os campos abaixo para criar um novo fabricante"
                iconType="info"
                loading={saving}
                loadingText="Salvando..."
                confirmButtonText="Salvar"
                cancelButtonText="Cancelar"
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                        <FormLabel component="legend" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                            Tipo de Veículo *
                        </FormLabel>
                        <RadioGroup
                            row
                            value={formData.type_vehicle}
                            onChange={(e) => setFormData({ ...formData, type_vehicle: e.target.value })}
                        >
                            <FormControlLabel value="car" control={<Radio />} label="Carro" />
                            <FormControlLabel value="motorcycle" control={<Radio />} label="Moto" />
                            <FormControlLabel value="bicycle" control={<Radio />} label="Bicicleta" />
                        </RadioGroup>
                    </FormControl>

                    <TextField
                        label="Nome do Fabricante *"
                        value={formData.name}
                        onChange={(e) => {
                            setFormData({ ...formData, name: e.target.value });
                            setFormErrors({ ...formErrors, name: false });
                        }}
                        fullWidth
                        variant="outlined"
                        sx={standaloneFieldSx}
                        error={formErrors.name}
                        helperText={formErrors.name ? 'Campo obrigatório' : ''}
                        disabled={saving}
                    />

                    <TextField
                        label="Descrição *"
                        value={formData.description}
                        onChange={(e) => {
                            setFormData({ ...formData, description: e.target.value });
                            setFormErrors({ ...formErrors, description: false });
                        }}
                        fullWidth
                        variant="outlined"
                        sx={standaloneFieldSx}
                        multiline
                        rows={3}
                        error={formErrors.description}
                        helperText={formErrors.description ? 'Campo obrigatório' : ''}
                        disabled={saving}
                    />
                </Box>
            </DialogForm>

            {/* Modal de Editar */}
            <DialogForm
                open={openEditModal}
                onClose={handleCloseEditModal}
                onSubmit={handleSubmitEdit}
                title="Editar Fabricante"
                subtitle="Altere os dados do fabricante"
                iconType="info"
                loading={saving}
                loadingText="Salvando..."
                confirmButtonText="Salvar"
                cancelButtonText="Cancelar"
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                        <FormLabel component="legend" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                            Tipo de Veículo *
                        </FormLabel>
                        <RadioGroup
                            row
                            value={formData.type_vehicle}
                            onChange={(e) => setFormData({ ...formData, type_vehicle: e.target.value })}
                        >
                            <FormControlLabel value="car" control={<Radio />} label="Carro" />
                            <FormControlLabel value="motorcycle" control={<Radio />} label="Moto" />
                            <FormControlLabel value="bicycle" control={<Radio />} label="Bicicleta" />
                        </RadioGroup>
                    </FormControl>

                    <TextField
                        label="Nome do Fabricante *"
                        value={formData.name}
                        onChange={(e) => {
                            setFormData({ ...formData, name: e.target.value });
                            setFormErrors({ ...formErrors, name: false });
                        }}
                        fullWidth
                        variant="outlined"
                        sx={standaloneFieldSx}
                        error={formErrors.name}
                        helperText={formErrors.name ? 'Campo obrigatório' : ''}
                        disabled={saving}
                    />

                    <TextField
                        label="Descrição *"
                        value={formData.description}
                        onChange={(e) => {
                            setFormData({ ...formData, description: e.target.value });
                            setFormErrors({ ...formErrors, description: false });
                        }}
                        fullWidth
                        variant="outlined"
                        sx={standaloneFieldSx}
                        multiline
                        rows={3}
                        error={formErrors.description}
                        helperText={formErrors.description ? 'Campo obrigatório' : ''}
                        disabled={saving}
                    />
                </Box>
            </DialogForm>

            {/* Confirmação de Exclusão */}
            <DialogConfirm
                open={openDeleteConfirm}
                onConfirm={handleDeleteConfirm}
                onCancel={() => {
                    setOpenDeleteConfirm(false);
                    setDeletingManufacturerId(null);
                }}
                message="Tem certeza que deseja excluir este fabricante? Esta ação não pode ser desfeita."
                title="Confirmar Exclusão"
            />

            {/* Snackbar para feedback */}
            <Snackbar
                open={openSnackBar}
                autoHideDuration={6000}
                onClose={() => setOpenSnackBar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setOpenSnackBar(false)}
                    severity={severity}
                    sx={{ width: '100%' }}
                >
                    {messageSnackBar}
                </Alert>
            </Snackbar>
        </>
    );
}
